import { memo } from 'react'
import { Link } from 'react-router-dom'
import rocketIllustration from '@/assets/1e2e5137-d063-41c3-bd2c-77bfa4e9de5a 1.svg'
import { goldGradientText, goldGradientButtonHero } from '@/lib/brandGradient'
import { cn } from '@/lib/cn'

type Props = {
  /** Total ideas submitted (matches hero “Ideas Submitted” — e.g. 111+ ideas). */
  ideasSubmittedTotal: number
}

export const IdeasMovingFastBanner = memo(({ ideasSubmittedTotal }: Props) => {
  const countLabel = Math.max(0, ideasSubmittedTotal).toLocaleString()

  return (
    <section
      className="w-full bg-[var(--background)] px-4 pt-6 pb-10 sm:px-6 sm:pt-8 sm:pb-12 lg:pt-10 lg:pb-14"
      aria-labelledby="ideas-moving-fast-heading"
    >
      <div className="mx-auto w-full max-w-[1200px]">
        <div
          className={cn(
            'relative w-full overflow-hidden rounded-[12px] max-sm:min-h-0 sm:h-[202px]',
            'border border-black/[0.06] bg-white shadow-[0_8px_28px_-12px_rgba(0,0,0,0.08)]',
            'dark:border-[rgba(255,255,255,0.08)] dark:bg-[#0b0b0b] dark:shadow-[0_10px_40px_-14px_rgba(0,0,0,0.45)] dark:ring-1 dark:ring-black/[0.06]',
          )}
        >
          <div
            className="pointer-events-none absolute inset-0 bg-gradient-to-r from-white via-white to-[#fafafa] dark:from-[#0b0b0b] dark:via-[#0b0b0b] dark:to-[#111010]"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute top-0 right-0 h-full w-[38%] bg-[radial-gradient(ellipse_70%_100%_at_100%_50%,rgba(224,162,99,0.12),transparent_72%)] max-sm:hidden dark:bg-[radial-gradient(ellipse_70%_100%_at_100%_50%,rgba(224,162,99,0.07),transparent_72%)]"
            aria-hidden
          />

          <div className="relative flex w-full flex-col gap-4 px-4 py-5 max-sm:py-5 sm:h-full sm:flex-row sm:items-stretch sm:gap-5 sm:px-6 sm:py-0 lg:px-8">
            <img
              src={rocketIllustration}
              alt=""
              aria-hidden
              className="mx-auto h-[88px] w-auto shrink-0 object-contain max-sm:self-center sm:mx-0 sm:h-[132px] sm:self-center"
              draggable={false}
            />

            <div className="flex min-w-0 flex-1 flex-col justify-center sm:py-4 sm:pr-2">
              <p
                id="ideas-moving-fast-heading"
                className={cn(
                  'text-[13px] font-medium tracking-[0.12em] uppercase max-sm:text-center sm:text-left sm:text-[14px] sm:tracking-[0.14em]',
                  goldGradientText,
                )}
              >
                IDEAS ARE MOVING FAST
              </p>
              <p className="mt-2 text-[15px] leading-[1.45] font-medium text-[var(--foreground)] max-sm:text-center sm:text-left sm:text-[16px] sm:leading-[1.5] dark:text-[#f4f4f5]">
                Our community has validated{' '}
                <span className={cn('inline font-medium', goldGradientText)}>
                  {countLabel}+ ideas
                </span>{' '}
                this week. Don&apos;t miss the opportunity to influence the{' '}
                <span className="whitespace-nowrap">next BIG one.</span>
                <br className="max-sm:hidden" />
                <span className="max-sm:mt-1 max-sm:block sm:inline">
                  The next game-changing idea needs early believers. Be part of
                  the journey.
                </span>
              </p>
            </div>

            <div className="flex shrink-0 max-sm:w-full max-sm:justify-center sm:items-end sm:pb-6">
              <Link
                to="/validate-ideas"
                className={`inline-flex h-[40px] w-full max-w-[200px] items-center justify-center text-sm font-semibold sm:w-[138px] sm:max-w-none ${goldGradientButtonHero}`}
              >
                Explore Ideas
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
})

IdeasMovingFastBanner.displayName = 'IdeasMovingFastBanner'
